import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import { Announcements } from '@air/api';
import { callAirApi } from '@air/utils-api';

import {
  type AnnouncementsListSuccess,
  type PrivateAnnouncementListItem,
} from '~/components/v2/hooks/queries/usePrivateAnnouncementsList';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { canViewAnnouncements } from '~/utils/permissions/workspacePermissions';

export const PRIVATE_ANNOUNCEMENTS_NEWS_LIST_KEY = 'PRIVATE_ANNOUNCEMENTS_NEWS_LIST';

export type PrivateAnnouncementsNewsListKey = [typeof PRIVATE_ANNOUNCEMENTS_NEWS_LIST_KEY, { workspaceId: string }];

export const getPrivateAnnouncementsNewsListKey = (workspaceId: string): PrivateAnnouncementsNewsListKey => {
  return [PRIVATE_ANNOUNCEMENTS_NEWS_LIST_KEY, { workspaceId }];
};

const ANNOUNCEMENTS_NEWS_PAGE_SIZE = {
  init: 30,
  more: 40,
};

const EMPTY_ANNOUNCEMENTS: PrivateAnnouncementListItem[] = [];

const getNewsListLimit = (pageParam: string | undefined) => {
  if (pageParam) {
    return ANNOUNCEMENTS_NEWS_PAGE_SIZE.more;
  }

  return ANNOUNCEMENTS_NEWS_PAGE_SIZE.init;
};

const selectAnnouncements = (data: InfiniteData<AnnouncementsListSuccess>) => data.pages.flatMap((page) => page.data);

export const usePrivateAnnouncementsNewsList = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { announcementsEnabled } = useTemporaryFeatureFlags();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canReadAnnouncements = canViewAnnouncements(permissions);

  const query = useInfiniteQuery({
    enabled: !!workspaceId && announcementsEnabled && canReadAnnouncements,
    getNextPageParam: (lastPage: AnnouncementsListSuccess) =>
      lastPage.pagination.hasMore ? (lastPage.pagination.cursor ?? undefined) : undefined,
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      if (!workspaceId) {
        throw new Error('workspaceId is required to list announcement news');
      }

      const cursor = pageParam && typeof pageParam === 'string' ? pageParam : undefined;

      return callAirApi<AnnouncementsListSuccess>(() =>
        Announcements.list({
          cursor,
          includeDismissed: true,
          includeExpired: true,
          limit: getNewsListLimit(cursor),
          workspaceId,
        }),
      );
    },
    queryKey: workspaceId ? getPrivateAnnouncementsNewsListKey(workspaceId) : [PRIVATE_ANNOUNCEMENTS_NEWS_LIST_KEY],
    select: selectAnnouncements,
  });

  return {
    announcements: query.data ?? EMPTY_ANNOUNCEMENTS,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isError: query.isError,
    isFetchNextPageError: query.isFetchNextPageError,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
